// Validated Textarea component with real-time validation and error display
import React from 'react';
import { RiErrorWarningLine, RiAlertLine } from 'react-icons/ri';

export interface ValidatedTextareaProps {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  error?: string | null;
  warning?: string | null;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  textareaClassName?: string;
  rows?: number;
  maxLength?: number;
  autoComplete?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}

const ValidatedTextarea: React.FC<ValidatedTextareaProps> = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  error,
  warning,
  required = false,
  disabled = false,
  placeholder,
  className = '',
  textareaClassName = '',
  rows = 3,
  maxLength,
  autoComplete,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  ...props
}) => {
  const hasError = Boolean(error);
  const hasWarning = Boolean(warning);

  const textareaId = `textarea-${name}`;
  const errorId = `${name}-error`;
  const warningId = `${name}-warning`;

  const getTextareaClassName = () => {
    let baseClassName = `mt-1.5 block w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 text-sm resize-vertical transition-all duration-150 ${textareaClassName}`;

    if (hasError) {
      baseClassName += ' border-danger-300 text-danger-900 placeholder:text-danger-300 focus:ring-danger-500/20 focus:border-danger-500 bg-danger-50/30';
    } else if (hasWarning) {
      baseClassName += ' border-warning-300 text-warning-900 placeholder:text-warning-300 focus:ring-warning-500/20 focus:border-warning-500 bg-warning-50/30';
    } else {
      baseClassName += ' border-secondary-300 text-secondary-900 placeholder:text-secondary-400 focus:ring-primary-500/20 focus:border-primary-500 bg-white';
    }

    if (disabled) {
      baseClassName += ' bg-secondary-50 text-secondary-400 cursor-not-allowed resize-none opacity-75';
    }

    return baseClassName;
  };

  const getAriaDescribedBy = () => {
    const describedBy = [];
    if (ariaDescribedBy) describedBy.push(ariaDescribedBy);
    if (hasError) describedBy.push(errorId);
    if (hasWarning) describedBy.push(warningId);
    return describedBy.length > 0 ? describedBy.join(' ') : undefined;
  };

  const currentLength = (value || '').length;
  const showCharacterCount = maxLength && maxLength > 0;
  const isNearLimit = showCharacterCount && currentLength > maxLength * 0.8;
  const isOverLimit = showCharacterCount && currentLength > maxLength;

  return (
    <div className={`${className}`}>
      <div className="flex justify-between items-center">
        <label htmlFor={textareaId} className="block text-xs font-semibold text-secondary-600 tracking-wide">
          {label}
          {required && <span className="text-danger-500 ml-0.5">*</span>}
        </label>

        {/* Character Count */}
        {showCharacterCount && (
          <span className={`text-xs ${
            isOverLimit
              ? 'text-danger-500'
              : isNearLimit
              ? 'text-warning-500'
              : 'text-secondary-500'
          }`}>
            {currentLength}/{maxLength}
          </span>
        )}
      </div>
      
      <div className="relative">
        <textarea
          id={textareaId}
          name={name}
          value={value || ''}
          onChange={onChange}
          onBlur={onBlur}
          // Note: Do NOT use required={required} as it triggers HTML5 native tooltip
          // Use inline error messages instead for consistent UX
          aria-required={required}
          disabled={disabled}
          placeholder={placeholder}
          className={getTextareaClassName()}
          rows={rows}
          maxLength={maxLength}
          autoComplete={autoComplete}
          aria-invalid={ariaInvalid || hasError}
          aria-describedby={getAriaDescribedBy()}
          {...props}
        />
        
        {/* Error/Warning Icon */}
        {(hasError || hasWarning) && (
          <div className="absolute top-3 right-3 pointer-events-none">
            {hasError ? (
              <RiErrorWarningLine className="h-5 w-5 text-danger-500" />
            ) : (
              <RiAlertLine className="h-5 w-5 text-warning-500" />
            )}
          </div>
        )}
      </div>

      {/* Error Message */}
      {hasError && (
        <div id={errorId} className="mt-1.5 flex items-center gap-1">
          <RiErrorWarningLine className="h-3.5 w-3.5 text-danger-500 flex-shrink-0" />
          <p className="text-xs text-danger-600">{error}</p>
        </div>
      )}

      {/* Warning Message */}
      {hasWarning && !hasError && (
        <div id={warningId} className="mt-1.5 flex items-center gap-1">
          <RiAlertLine className="h-3.5 w-3.5 text-warning-500 flex-shrink-0" />
          <p className="text-xs text-warning-700">{warning}</p>
        </div>
      )}
    </div>
  );
};

export default ValidatedTextarea;